import styled from 'styled-components'

import { barnesTheme } from '../../theme/barnesTheme'

export const TwoColumnLayout = styled.div`
  display: flex;
  gap: 0;
  margin-top: ${barnesTheme.spacing.contentGap};

  @media print {
    display: flex !important;
    gap: 0 !important;
    margin-top: ${barnesTheme.spacing.contentGap} !important;
  }
`

export const PhotoPlaceholder = styled.div<{ $clickable?: boolean }>`
  width: 50%;
  min-width: 0;
  flex-shrink: 0;
  align-self: stretch;
  background: ${barnesTheme.colors.grege};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  @media print {
    width: 50% !important;
    min-width: 0 !important;
    flex-shrink: 0 !important;
    align-self: stretch !important;
    background: ${barnesTheme.colors.grege} !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    overflow: hidden !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;

    /* No photo → print blank (no grège frame, no "Photo du bien" label). */
    &:not(:has(img)) {
      background: transparent !important;
    }
  }
`

export const PropertyPhoto = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;

  @media print {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover !important;
  }
`

export const PhotoPlaceholderText = styled.span`
  font-family: ${barnesTheme.fonts.body};
  font-size: ${barnesTheme.fontSizes.small};
  color: ${barnesTheme.colors.textLight};

  /* Hidden in the PDF — an absent cover photo stays blank, no label. */
  @media print {
    display: none !important;
  }
`

export const SpecsColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 0 0 0 ${barnesTheme.spacing.contentGap};

  @media print {
    flex: 1 !important;
    display: flex !important;
    flex-direction: column !important;
    padding: 0 0 0 ${barnesTheme.spacing.contentGap} !important;
  }
`

export const SpecsTitle = styled.h3`
  font-family: ${barnesTheme.fonts.heading};
  font-size: ${barnesTheme.fontSizes.xlarge};
  font-weight: ${barnesTheme.fontWeights.bold};
  color: ${barnesTheme.colors.primary};
  margin: 0 0 ${barnesTheme.spacing.smallGap} 0;

  @media print {
    font-size: ${barnesTheme.fontSizes.xlarge} !important;
    font-weight: ${barnesTheme.fontWeights.bold} !important;
    color: ${barnesTheme.colors.primary} !important;
    margin: 0 0 ${barnesTheme.spacing.smallGap} 0 !important;
  }
`

export const SpecsTitleHairline = styled.div`
  width: 100%;
  height: 1px;
  background: ${barnesTheme.colors.border};
  margin-bottom: ${barnesTheme.spacing.smallGap};

  @media print {
    width: 100% !important;
    height: 1px !important;
    background: ${barnesTheme.colors.border} !important;
    margin-bottom: ${barnesTheme.spacing.smallGap} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const SpecRow = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 6px;
  margin-bottom: 6px;
  border-bottom: 1px solid ${barnesTheme.colors.border};

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }

  @media print {
    display: flex !important;
    flex-direction: column !important;
    padding-bottom: 6px !important;
    margin-bottom: 6px !important;
    border-bottom: 1px solid ${barnesTheme.colors.border} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;

    &:last-child {
      border-bottom: none !important;
      margin-bottom: 0 !important;
      padding-bottom: 0 !important;
    }
  }
`

export const SpecLabel = styled.span`
  font-family: ${barnesTheme.fonts.body};
  font-size: ${barnesTheme.fontSizes.small};
  font-weight: ${barnesTheme.fontWeights.light};
  color: ${barnesTheme.colors.textLight};

  @media print {
    font-size: ${barnesTheme.fontSizes.small} !important;
    font-weight: ${barnesTheme.fontWeights.light} !important;
    color: ${barnesTheme.colors.textLight} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const SpecValue = styled.span`
  font-family: ${barnesTheme.fonts.body};
  font-size: 13px;
  font-weight: ${barnesTheme.fontWeights.bold};
  color: ${barnesTheme.colors.primary};
  margin-top: 2px;

  @media print {
    font-size: 13px !important;
    font-weight: ${barnesTheme.fontWeights.bold} !important;
    color: ${barnesTheme.colors.primary} !important;
    margin-top: 2px !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const FeaturesRow = styled.div`
  margin-top: ${barnesTheme.spacing.contentGap};

  @media print {
    margin-top: ${barnesTheme.spacing.contentGap} !important;
  }
`

export const FeaturesText = styled.span`
  font-family: ${barnesTheme.fonts.body};
  font-size: ${barnesTheme.fontSizes.small};
  font-weight: ${barnesTheme.fontWeights.light};
  color: ${barnesTheme.colors.textLight};
  line-height: 1.6;

  @media print {
    font-size: ${barnesTheme.fontSizes.small} !important;
    font-weight: ${barnesTheme.fontWeights.light} !important;
    color: ${barnesTheme.colors.textLight} !important;
    line-height: 1.6 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const PointsSection = styled.div`
  margin-top: ${barnesTheme.spacing.contentGap};

  @media print {
    margin-top: ${barnesTheme.spacing.contentGap} !important;
  }
`

export const PointsTitle = styled.span`
  font-family: ${barnesTheme.fonts.heading};
  font-size: 13px;
  font-weight: ${barnesTheme.fontWeights.bold};
  color: ${barnesTheme.colors.accent};
  display: block;
  margin-bottom: 6px;

  @media print {
    font-size: 13px !important;
    font-weight: ${barnesTheme.fontWeights.bold} !important;
    color: ${barnesTheme.colors.accent} !important;
    display: block !important;
    margin-bottom: 6px !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const PointsHairline = styled.div`
  width: 100%;
  height: 1px;
  background: ${barnesTheme.colors.border};
  margin: ${barnesTheme.spacing.smallGap} 0;

  @media print {
    width: 100% !important;
    height: 1px !important;
    background: ${barnesTheme.colors.border} !important;
    margin: ${barnesTheme.spacing.smallGap} 0 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const PointsList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;

  @media print {
    margin: 0 !important;
    padding: 0 !important;
    list-style: none !important;
  }
`

export const PointItem = styled.li`
  font-family: ${barnesTheme.fonts.body};
  font-size: ${barnesTheme.fontSizes.body};
  font-weight: ${barnesTheme.fontWeights.light};
  color: ${barnesTheme.colors.text};
  line-height: 1.8;

  @media print {
    font-size: ${barnesTheme.fontSizes.body} !important;
    font-weight: ${barnesTheme.fontWeights.light} !important;
    color: ${barnesTheme.colors.text} !important;
    line-height: 1.8 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const DescriptionBlock = styled.div`
  margin-top: ${barnesTheme.spacing.sectionGap};

  @media print {
    margin-top: ${barnesTheme.spacing.sectionGap} !important;
  }
`

export const DescriptionTitle = styled.h3`
  font-family: ${barnesTheme.fonts.heading};
  font-size: 13px;
  font-weight: ${barnesTheme.fontWeights.bold};
  color: ${barnesTheme.colors.accent};
  margin: ${barnesTheme.spacing.sectionGap} 0 ${barnesTheme.spacing.smallGap} 0;

  &:first-child {
    margin-top: 0;
  }

  @media print {
    font-size: 13px !important;
    font-weight: ${barnesTheme.fontWeights.bold} !important;
    color: ${barnesTheme.colors.accent} !important;
    margin: ${barnesTheme.spacing.sectionGap} 0 ${barnesTheme.spacing.smallGap}
      0 !important;

    &:first-child {
      margin-top: 0 !important;
    }
  }
`

export const DescriptionText = styled.div`
  font-family: ${barnesTheme.fonts.body};
  font-size: ${barnesTheme.fontSizes.body};
  font-weight: ${barnesTheme.fontWeights.light};
  color: ${barnesTheme.colors.text};
  line-height: 1.8;

  p {
    margin: 0 0 ${barnesTheme.spacing.contentGap} 0;
    &:last-child {
      margin-bottom: 0;
    }
  }

  strong {
    font-weight: ${barnesTheme.fontWeights.bold};
    color: ${barnesTheme.colors.primary};
  }

  em {
    font-style: italic;
  }

  @media print {
    font-size: ${barnesTheme.fontSizes.body} !important;
    font-weight: ${barnesTheme.fontWeights.light} !important;
    color: ${barnesTheme.colors.text} !important;
    line-height: 1.6 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

/* Legacy exports kept for backwards compatibility */
export const TableColumn = SpecsColumn
export const TableRow = SpecRow
export const TableLabel = SpecLabel
export const TableValue = SpecValue
export const FeatureTag = FeaturesText
export const CardsRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`
export const StrengthsCard = PointsSection
export const WeaknessesCard = PointsSection
export const CardSubtitleBar = PointsHairline
export const BulletList = PointsList
export const BulletItem = PointItem
